// [목적] user 도메인 REST API 클라이언트 — /users/* 엔드포인트 매핑 (liked·forked·deleteMe 추가)
// [이유] fe-be-wire-up Wave 0 INFRA-04 — ProfilePage·MyFoliosPage·팔로우 토글에 필요한 모든
//         user 엔드포인트를 단일 모듈로 캡슐화. 컴포넌트가 fetch 를 직접 호출하지 않도록 강제.
//         fe-be-gap-tier1 T7(ADR-TR-4): GET /users/me/liked · /users/me/forked 클라이언트 추가.
//         fe-be-gap-tier2 T16(R-2): folioSchema.viewCount/author/budgetBreakdown 추가 후 liked/forked 정합 확인.
//           folioListSchema(item=folioSchema) → viewCount(optional default 0)·author(nullable optional)·
//           budgetBreakdown(nullable optional) 모두 optional/nullable 이라 parse 실패 없음 — Tier 1 R-3 우회 해소.
//           userFolioSnapshotSchema(getUserFolios GET /:handle/folios) 는 경량 스냅샷이므로 별도 유지.
//         fe-be-gap-tier3 T13(ADR-T3-4/-5): DELETE /users/me 회원 탈퇴 클라이언트 추가.
//           soft-delete + 본인 폴리오 private 전환 + 세션 무효화 + 쿠키 삭제 (BE 처리).
//           FE 는 성공 시 window.location.href='/login' 으로 완전 reload(useAuth.logout 패턴 동일).
// [사이드 이팩트] BE UsersController 의 정확한 path/method/DTO 와 1:1 매핑.
//                 D-3 drift: api_spec.md 는 /users/me/follow/{targetUserId}(UUID) 이나 실 BE 는 :targetHandle (handle)
//                 → 실 BE 를 SoT 로 handle 사용. api_spec.md 보정은 후속 api-spec-drift-reconcile Spec.
//                 GET /users/me 와 GET /auth/me 는 별도 엔드포인트 — user.me()는 UserMe 스키마 (공개+민감필드),
//                 auth.me()는 MeOutput 스키마 (인터레스트/마케팅/온보딩 포함)
// [수정 시 고려사항] 팔로우/언팔 엔드포인트는 JwtAuthGuard + CsrfGuard 적용 — http.ts 가 CSRF 헤더 자동 첨부.
//                   GET /users/:handle 은 AnonOrAuthenticatedGuard — anon 접근 허용.
//                   배지·liked·forked 목록은 JwtAuthGuard 필수 — 비로그인 401.
//                   배지 목록은 cursor/limit 쿼리 지원 (기본 20).
//                   getUserFolios → userFolioSnapshotSchema(경량): viewCount 포함(BE serializeFolio 반환 시 일치).
//                     BE listByHandle 가 serializeFolio 를 사용하면 viewCount 포함 → optional 필드이므로 정합.

import {
  userPublicProfileSchema,
  userMeSchema,
  patchUserMeInputSchema,
  followResponseSchema,
  followListQuerySchema,
  badgeSchema,
  folioListSchema,
  userStatsSchema,
  avatarUploadUrlInputSchema,
  avatarUploadUrlResponseSchema,
  type UserPublicProfile,
  type UserMe,
  type PatchUserMeInput,
  type FollowResponse,
  type FollowListQuery,
  type Badge,
  badgeCatalogSchema,
  type BadgeCatalog,
  type FolioList,
  type UserStats,
  type AvatarUploadUrlInput,
  type AvatarUploadUrlResponse,
} from '@gangwoncanvas/types';
import { z } from 'zod';
import { fetchWithEnvelope } from './http.js';

// ─── 내부 헬퍼 ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ─── 페이지네이션 응답 스키마 ──────────────────────────────────────────────────

/**
 * [목적] cursor 기반 페이지네이션 응답 래퍼 스키마
 * [이유] GET /users/:handle/folios · /followers · /following 공통 구조
 * [사이드 이팩트] nextCursor=null 이면 마지막 페이지
 * [수정 시 고려사항] BE 응답 구조 변경 시 동기 갱신 필요
 */
function cursorPageSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema),
    nextCursor: z.string().nullable(),
    total: z.number().int().nonnegative().optional(),
  });
}

// 공개 프로필 경량 스냅샷 (팔로워/팔로잉 목록용)
const userSnapshotSchema = z.object({
  id: z.string().uuid(),
  handle: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().url().nullable(),
  isFollowing: z.boolean(),
});

// 폴리오 경량 스냅샷 (사용자 폴리오 목록용)
const userFolioSnapshotSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  visibility: z.enum(['public', 'link', 'private']),
  likeCount: z.number().int().nonnegative(),
  bookmarkCount: z.number().int().nonnegative(),
  viewCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const badgeListResponseSchema = z.object({
  items: z.array(badgeSchema),
  nextCursor: z.string().nullable(),
});

export type UserFolioSnapshot = z.infer<typeof userFolioSnapshotSchema>;
export type UserFollowPage = z.infer<
  ReturnType<typeof cursorPageSchema<typeof userSnapshotSchema>>
>;
export type UserFoliosPage = z.infer<
  ReturnType<typeof cursorPageSchema<typeof userFolioSnapshotSchema>>
>;

// ─── API 메서드 ────────────────────────────────────────────────────────────────

/**
 * [목적] GET /users/:handle — 공개 프로필 조회 (비인증 허용)
 * [이유] INFRA-04: ProfilePage(`/u/:handle`) 에서 호출. AnonOrAuthenticatedGuard 적용
 * [사이드 이팩트] isMe 필드로 FE 에서 편집 버튼 노출 여부 분기
 * [수정 시 고려사항] 없는 handle 은 BE 가 404 반환
 */
export async function getPublicProfile(handle: string): Promise<UserPublicProfile> {
  const data = await fetchWithEnvelope<unknown>(`/users/${handle}`);
  return userPublicProfileSchema.parse(data);
}

/**
 * [목적] GET /users/me — 본인 프로필 (이메일 마스킹 포함 self-view)
 * [이유] INFRA-04: MyPage·Settings 에서 본인 정보 표시용. JwtAuthGuard 필수
 * [사이드 이팩트] userMeSchema 파싱 — 민감 필드(emailMasked) 포함
 * [수정 시 고려사항] auth.client.ts 의 me() (GET /auth/me) 와는 다른 엔드포인트
 */
export async function getMe(): Promise<UserMe> {
  const data = await fetchWithEnvelope<unknown>('/users/me');
  return userMeSchema.parse(data);
}

/**
 * [목적] PATCH /users/me — 본인 정보 수정
 * [이유] INFRA-04: ProfileEditPage, Settings 에서 호출. JwtAuthGuard + CsrfGuard 필수
 * [사이드 이팩트] handle 변경 불가 (patchUserMeInputSchema 에 handle 미포함). notificationPrefs partial merge
 * [수정 시 고려사항] 입력은 patchUserMeInputSchema 로 FE 에서도 선검증
 */
export async function patchMe(input: PatchUserMeInput): Promise<UserMe> {
  const validated = patchUserMeInputSchema.parse(input);
  const data = await fetchWithEnvelope<unknown>('/users/me', {
    method: 'PATCH',
    body: JSON.stringify(validated),
  });
  return userMeSchema.parse(data);
}

/**
 * [목적] GET /users/me/stats — 홈 통계 카드(내 폴리오/받은 좋아요/Fork됨) 실데이터
 * [이유] M7 P1 — HomePage 통계 카드 mock(0/221/34) 대체. JwtAuthGuard 필수(BE users.controller.ts).
 * [사이드 이팩트] 비로그인 시 401 — 호출부가 enabled=status==='authenticated' 로 가드해야 함.
 * [수정 시 고려사항] 응답 shape 은 packages/types userStatsSchema 와 1:1(folioCount/likesReceived/forkedCount).
 */
export async function getMyStats(): Promise<UserStats> {
  const data = await fetchWithEnvelope<unknown>('/users/me/stats');
  return userStatsSchema.parse(data);
}

/**
 * [목적] GET /users/me/badges — 본인 보유 배지 목록 (cursor 페이지네이션)
 * [이유] INFRA-04: ProfilePage 배지 탭에서 호출. JwtAuthGuard 필수
 * [사이드 이팩트] badgeSchema 배열로 파싱. cursor/limit 쿼리 지원
 * [수정 시 고려사항] limit 기본값 20, 최대 50 (BE 서버사이드 제한과 동일)
 */
export async function getMyBadges(opts?: {
  cursor?: string;
  limit?: number;
}): Promise<{ items: Badge[]; nextCursor: string | null }> {
  const q = buildQuery({
    cursor: opts?.cursor,
    limit: opts?.limit,
  });
  const data = await fetchWithEnvelope<unknown>(`/users/me/badges${q}`);
  return badgeListResponseSchema.parse(data);
}

/**
 * [목적] GET /users/me/badges/catalog — 전체 배지 도감(획득 + 미획득 + 진행률)
 * [이유] 2026-09-21 badge-catalog: 프로필 뱃지 탭이 보유 배지만 보여줘 "무엇을 더 모을 수 있는지"가
 *         화면에 없었다. 잠금 슬롯을 FE mock 으로 만드는 것은 ADR-T1-5 위반이라 BE 카탈로그를 쓴다.
 * [사이드 이팩트] JwtAuthGuard 필수(비로그인 401). 조회 시점에 조건 충족 배지가 부여될 수 있다.
 * [수정 시 고려사항] 응답 shape 은 packages/types badgeCatalogSchema 와 1:1 — 라벨/조건 문구는 BE 소유.
 */
export async function getMyBadgeCatalog(): Promise<BadgeCatalog> {
  const data = await fetchWithEnvelope<unknown>('/users/me/badges/catalog');
  return badgeCatalogSchema.parse(data);
}

/**
 * [목적] GET /users/me/liked — 본인이 좋아요한 폴리오 목록 (cursor 페이지네이션)
 * [이유] fe-be-gap-tier1 T7(ADR-TR-4·ADR-T1-2): Profile liked 탭 실데이터 연결.
 *         JwtAuthGuard 필수 — 비로그인 401. 응답은 folioListSchema(item=folioSchema) 파싱.
 *         fe-be-gap-tier2 T16(R-2): folioSchema.viewCount(optional default 0)·author(nullable optional)·
 *           budgetBreakdown(nullable optional) 가 모두 optional/nullable 이라 parse 실패 없음.
 *           Tier 1 R-3 "userFolioSnapshotSchema 재사용 금지" 우회 불필요 — folioListSchema 그대로 사용.
 * [사이드 이팩트] BE serializeFolio 단일 진입점이면 viewCount/author 자동 포함(R-2).
 * [수정 시 고려사항] 좋아요한 역순(folio_likes.created_at DESC)으로 커서 페이지네이션.
 *                   원본 폴리오 soft-delete 시 BE 가 필터링.
 */
export async function getMyLiked(opts?: { cursor?: string; limit?: number }): Promise<FolioList> {
  const q = buildQuery({
    cursor: opts?.cursor,
    limit: opts?.limit,
  });
  const data = await fetchWithEnvelope<unknown>(`/users/me/liked${q}`);
  return folioListSchema.parse(data);
}

/**
 * [목적] GET /users/me/forked — 본인이 Fork 해 만든 사본 폴리오 목록 (cursor 페이지네이션)
 * [이유] fe-be-gap-tier1 T7(ADR-TR-1·ADR-T1-1·ADR-T1-2): Profile forks 탭 실데이터 연결.
 *         "내 fork 사본"(owner_user_id=me, source='fork') 의미론 — ADR-T1-1 확정.
 *         JwtAuthGuard 필수 — 비로그인 401. 응답은 folioListSchema(item=folioSchema) 파싱.
 *         fe-be-gap-tier2 T16(R-2): folioSchema 확장 필드 optional/nullable 이라 parse 실패 없음.
 *           Tier 1 R-3 "userFolioSnapshotSchema 재사용 금지" 우회 해소 — folioListSchema 사용.
 * [사이드 이팩트] fork 이력 폴리오(forkFull/forkDayImport 결과 사본)만 포함.
 *                 원본 폴리오 soft-delete 시 BE 가 필터링.
 * [수정 시 고려사항] BE serializeFolio 가 liked/forked 응답에도 사용되면 viewCount/author 자동 포함(R-2).
 */
export async function getMyForked(opts?: { cursor?: string; limit?: number }): Promise<FolioList> {
  const q = buildQuery({
    cursor: opts?.cursor,
    limit: opts?.limit,
  });
  const data = await fetchWithEnvelope<unknown>(`/users/me/forked${q}`);
  return folioListSchema.parse(data);
}

/**
 * [목적] GET /users/me/bookmarks — 본인이 북마크한 폴리오 목록 (cursor 페이지네이션)
 * [이유] G2-3: 계약 C3 — ProfileMobile 북마크 탭 mock(BOOKMARKS 배열) 제거를 위한 실데이터 소스.
 *         getMyLiked 와 동일한 응답 형태(folioListSchema)·커서 방식(users.controller.ts 주석 근거).
 *         JwtAuthGuard 필수 — 비로그인 401.
 * [사이드 이팩트] 없음(조회 전용).
 * [수정 시 고려사항] 커서 포맷은 bookmarked 전용(liked 커서와 호환 불가, BE 주석 근거) — 다른 탭과
 *                   커서를 섞어 쓰지 말 것(탭별 useInfiniteQuery 캐시 분리로 자연히 보장됨).
 */
export async function getMyBookmarks(opts?: {
  cursor?: string;
  limit?: number;
}): Promise<FolioList> {
  const q = buildQuery({
    cursor: opts?.cursor,
    limit: opts?.limit,
  });
  const data = await fetchWithEnvelope<unknown>(`/users/me/bookmarks${q}`);
  return folioListSchema.parse(data);
}

/**
 * [목적] GET /users/:handle/folios — 핸들 기준 폴리오 목록 (비인증 허용)
 * [이유] INFRA-04: ProfilePage 폴리오 탭, MyFoliosPage 에서 호출. AnonOrAuthenticatedGuard 적용
 * [사이드 이팩트] visibility 에 따라 비로그인 사용자에게 private 폴리오 미노출 (BE 서버사이드 필터)
 * [수정 시 고려사항] cursor/limit 쿼리 파라미터 지원
 */
export async function getUserFolios(
  handle: string,
  q?: { cursor?: string; limit?: number },
): Promise<UserFoliosPage> {
  const query = buildQuery({ cursor: q?.cursor, limit: q?.limit });
  const data = await fetchWithEnvelope<unknown>(`/users/${handle}/folios${query}`);
  return cursorPageSchema(userFolioSnapshotSchema).parse(data);
}

/**
 * [목적] GET /users/:handle/followers — 팔로워 목록 (비인증 허용)
 * [이유] INFRA-04: ProfilePage 팔로워 탭에서 호출. AnonOrAuthenticatedGuard 적용
 * [사이드 이팩트] cursor 페이지네이션. isFollowing 필드로 팔로우 토글 UI 분기
 * [수정 시 고려사항] FollowListQuery 타입으로 cursor/limit 검증
 */
export async function getFollowers(
  handle: string,
  q?: Partial<FollowListQuery>,
): Promise<UserFollowPage> {
  const validated = q ? followListQuerySchema.partial().parse(q) : {};
  const query = buildQuery(validated as Record<string, string | number | undefined>);
  const data = await fetchWithEnvelope<unknown>(`/users/${handle}/followers${query}`);
  return cursorPageSchema(userSnapshotSchema).parse(data);
}

/**
 * [목적] GET /users/:handle/following — 팔로잉 목록 (비인증 허용)
 * [이유] INFRA-04: ProfilePage 팔로잉 탭에서 호출. AnonOrAuthenticatedGuard 적용
 * [사이드 이팩트] cursor 페이지네이션
 * [수정 시 고려사항] cursor/limit 기본값은 BE 서버사이드 적용
 */
export async function getFollowing(
  handle: string,
  q?: Partial<FollowListQuery>,
): Promise<UserFollowPage> {
  const validated = q ? followListQuerySchema.partial().parse(q) : {};
  const query = buildQuery(validated as Record<string, string | number | undefined>);
  const data = await fetchWithEnvelope<unknown>(`/users/${handle}/following${query}`);
  return cursorPageSchema(userSnapshotSchema).parse(data);
}

/**
 * [목적] POST /users/me/follow/:targetHandle — 팔로우 (idempotent)
 * [이유] INFRA-04: ProfilePage 팔로우 버튼에서 호출. JwtAuthGuard + CsrfGuard 필수
 * [사이드 이팩트] D-3 drift — 실 BE 는 targetHandle (handle) 사용. api_spec.md 에 targetUserId(UUID) 기재됨 (후속 drift-reconcile)
 * [수정 시 고려사항] alreadyFollowing=true 시 idempotent 200 (중복 팔로우 무시)
 */
export async function follow(targetHandle: string): Promise<FollowResponse> {
  const data = await fetchWithEnvelope<unknown>(`/users/me/follow/${targetHandle}`, {
    method: 'POST',
  });
  return followResponseSchema.parse(data);
}

/**
 * [목적] DELETE /users/me/follow/:targetHandle — 언팔로우
 * [이유] INFRA-04: ProfilePage 팔로우 토글에서 호출. JwtAuthGuard + CsrfGuard 필수
 * [사이드 이팩트] 팔로우 관계 없을 시 BE idempotent 200 반환
 * [수정 시 고려사항] 동일 D-3 drift 적용 — handle 사용
 */
export async function unfollow(targetHandle: string): Promise<FollowResponse> {
  const data = await fetchWithEnvelope<unknown>(`/users/me/follow/${targetHandle}`, {
    method: 'DELETE',
  });
  return followResponseSchema.parse(data);
}

/**
 * [목적] DELETE /users/me — 회원 탈퇴 (soft-delete)
 * [이유] fe-be-gap-tier3 T13(ADR-T3-4/-5) — 실 탈퇴 플로우 활성화.
 *         BE: users.deleted_at=now() + 본인 폴리오 visibility='private' + refresh 토큰 revoke + 쿠키 삭제.
 *         JwtAuthGuard + CsrfGuard 적용 — http.ts 가 CSRF 헤더(gw_csrf) 자동 첨부.
 * [사이드 이팩트] 204 응답 시 http.ts 가 undefined 반환(void 처리). 성공 후 FE 는 window.location.href='/login'.
 *                 handle 재사용 불가(ADR-T3-5): 탈퇴 후에도 UNIQUE 점유 유지.
 * [수정 시 고려사항] 탈퇴 후 access token 15분 잔존 창은 refresh revoke + findById deleted_at 필터로 차단(ADR-TR3-7).
 *                   완전한 블랙리스트 차단은 후속 Spec(ADR-TR3-7 확정).
 */
export async function deleteMe(): Promise<void> {
  await fetchWithEnvelope<unknown>('/users/me', {
    method: 'DELETE',
  });
}

/**
 * [목적] POST /users/me/avatar-upload-url — 프로필 사진·커버 이미지 업로드용 presigned PUT URL 발급
 * [이유] FlowLine b609867a 잔여(프로필 사진·커버 업로드) — ProfileEditScreen 이 이 URL 로 브라우저에서
 *        S3(호환) 버킷에 직접 PUT 업로드하고, 완료 후 publicUrl 로 patchMe({avatarUrl|coverUrl})를 호출한다.
 * [사이드 이팩트] JwtAuthGuard+CsrfGuard 필수 — http.ts 가 CSRF 헤더 자동 첨부. S3 미설정 환경에서는
 *                BE 가 501(user.IMAGE_UPLOAD_UNAVAILABLE)을 반환한다(ApiError.status===501 로 분기).
 * [수정 시 고려사항] contentType/sizeBytes 상한은 avatarUploadUrlInputSchema(kind 별)로 클라이언트 선검증됨 —
 *                   BE 상한과 반드시 동일한 스키마(단일 소스)를 재사용하므로 값 변경 시 자동 동기화.
 */
export async function requestAvatarUploadUrl(
  input: AvatarUploadUrlInput,
): Promise<AvatarUploadUrlResponse> {
  const validated = avatarUploadUrlInputSchema.parse(input);
  const data = await fetchWithEnvelope<unknown>('/users/me/avatar-upload-url', {
    method: 'POST',
    body: JSON.stringify(validated),
  });
  return avatarUploadUrlResponseSchema.parse(data);
}
